import { type CSSProperties } from "react";
import { Page } from "lola-framework-ui-test";
import { Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RotateToPortraitOverlay } from "./components/RotateToPortraitOverlay";
import { useReadTheme } from "./hooks/useReadTheme";
import { useMaintenanceWatch } from "./hooks/useMaintenanceWatch";
import { useSessionExpiredHandler } from "./hooks/useSessionExpiredHandler";
import { OfflineOverlay } from "./components/OfflineOverlay";
import { useLinkExpiryWatch } from "./hooks/useLinkExpiryWatch";
import { useTabResumeLinkLiveness } from "./hooks/useTabResumeLinkLiveness";
import { useLanguageSync } from "./hooks/useLanguageSync";
import { useSessionBootstrap } from "./hooks/useSessionBootstrap";
import { useStore } from "./store/globalState";
import { routesList } from "./utils";
import { NovuInboxProvider } from "./components/notifications/NovuInboxProvider";
import { isFlavorB } from "./constants";

const App = () => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { isLoadingTheme, reedTheme } = useReadTheme();

  // Purge stale session on cold link change; restore widget link id after refresh.
  useSessionBootstrap();

  // Handle session expired events globally
  useSessionExpiredHandler();
  useMaintenanceWatch();
  useTabResumeLinkLiveness();
  useLinkExpiryWatch();

  // Re-sync i18n language from persisted session after page refresh
  useLanguageSync();

  return (
    <Page
      font={{
        cdn: reedTheme.font.fontcdn,
        name: reedTheme.font.fontfamily,
      }}
    >
      <section
        style={
          {
            "--border-radius": isFlavorB ? "6px" : "16px",
          } as CSSProperties
        }
      >
        {isLoadingTheme && <p>Loading theme...</p>}
        {!theme && <p>Theme not loaded</p>}
        <NovuInboxProvider>
          <Routes>
            {routesList.map(({ path, Component }) => (
              <Route key={path} path={path} Component={Component} />
            ))}
          </Routes>
        </NovuInboxProvider>
      </section>
      <RotateToPortraitOverlay
        title={t("orientation.title")}
        subtitle={t("orientation.subtitle")}
      />
      <OfflineOverlay />
    </Page>
  );
};

export default App;
